// MA-1044 — the reading placement ladder as a LEAF module (moved verbatim
// from readingDiagnosticContent.ts, which re-exports every symbol so its
// importers are unchanged). This file imports no content pack, so the
// readiness check (/check) can resolve a terminal rung to its real
// READING_MODULES id in its lean entry chunk; the items themselves still come
// from readingDiagnosticContent.ts (the real content reader), loaded lazily.
// Keep it a leaf: type-only imports plus adaptiveStaircase's constants.
import type { ContentPackName } from "../curriculum/types/content";
import {
  STAIRCASE_MIN_ITEMS,
  STAIRCASE_MAX_ITEMS,
  STAIRCASE_REVERSAL_STOP_COUNT,
} from "./adaptiveStaircase";

interface ReadingRung {
  skillId: string;
  pack: ContentPackName;
  moduleId: string;
  /** READING_MODULES difficulty (0-8) this rung serves. */
  moduleDifficulty: number;
}

export const READING_DIAGNOSTIC_LADDER: ReadingRung[] = [
  { skillId: "ms.read.0a", pack: "rhyming", moduleId: "reading.0a", moduleDifficulty: 0 },
  { skillId: "ms.read.0b", pack: "syllable_counting", moduleId: "reading.0b", moduleDifficulty: 1 },
  { skillId: "ms.read.06", pack: "controlledSentences", moduleId: "reading.06", moduleDifficulty: 3 },
  { skillId: "ms.read.09", pack: "controlledSentences", moduleId: "reading.09", moduleDifficulty: 5 },
  { skillId: "ms.read.11", pack: "controlledSentences", moduleId: "reading.11", moduleDifficulty: 6 },
  { skillId: "ms.read.12", pack: "controlledSentences", moduleId: "reading.12", moduleDifficulty: 7 },
  { skillId: "ms.read.15", pack: "controlledSentences", moduleId: "reading.15", moduleDifficulty: 8 },
];

// v1 calibration: reuse adaptiveStaircase's own shared constants rather than
// invent untested new ones. They were sized for a 9-rung span; this 7-rung
// ladder is narrower, so the same MIN/MAX budget is a conservative
// (over-, never under-) exposure bound.
export const READING_DIAGNOSTIC_STAIRCASE_CONFIG = {
  ladderSize: READING_DIAGNOSTIC_LADDER.length,
  seedRung: 0,
  minItems: STAIRCASE_MIN_ITEMS,
  maxItems: STAIRCASE_MAX_ITEMS,
  reversalStopCount: STAIRCASE_REVERSAL_STOP_COUNT,
};

/** MA-301 — the real READING_MODULES id a finished staircase's terminal rung
 *  maps to (same clamp as readingDiagnosticItemAt, since finalRung is the
 *  same rung-index space). Used to derive an evidence-backed placement
 *  floor, never to seed mastery — see placementFloor.ts's own doctrine. */
export function readingModuleIdForRung(rungIndex: number): string {
  const clamped = Math.max(0, Math.min(READING_DIAGNOSTIC_LADDER.length - 1, rungIndex));
  return READING_DIAGNOSTIC_LADDER[clamped].moduleId;
}

